import { compressImageIfNeeded } from './utils.mjs';
import { showReceiptScannerModal } from './receiptScannerModal.mjs';
import { Icons } from './components.mjs';
import t from 'virtual:i18n';

/**
 * Calculator pages' "scan a receipt" button (built per locale by
 * build-landing.mjs as scan.bundle.js). One call does everything: with no
 * groupId, createExpenseFromReceipt extracts the receipt, creates a group
 * named after the merchant in the page's locale currency, adds the itemized
 * expense, and returns the group - the page then lands on the app's receipt
 * screen where the visitor taps who had what. Downsizing, the scanner
 * overlay (the receipt with the sweeping laser - its CSS is
 * /tabs/receipt-scanner.css, linked by the page) and the API call are the
 * app's own; nothing here knows about receipts.
 *
 * Markup contract (see landing/ja/warikan-keisan/index.html):
 *   <input type="file" id="calcScanInput" accept="image/*" hidden>
 *   <button id="calcScanBtn" data-error="…" data-limit="…">label</button>
 * The camera glyph (the app's shared icon) is prepended here so the page's
 * static markup never carries a copy of the SVG.
 */
// The locales.mjs code: data-locale when the page carries it (lang and code differ for zh/zh-Hans),
// else the whole lang tag lowercased (<html lang="zh-Hant"> -> 'zh-hant').
const LANG = document.documentElement.dataset.locale || (document.documentElement.lang || 'en').toLowerCase();
const PREFIX = LANG === 'en' ? '' : `/${LANG}`;

const input = document.getElementById('calcScanInput');
const btn = document.getElementById('calcScanBtn');

if (input && btn) {
    btn.insertAdjacentHTML('afterbegin', `<span class="calc-scan-icon" aria-hidden="true">${Icons.camera}</span>`);
    btn.addEventListener('click', () => input.click());
    input.addEventListener('change', async () => {
        const file = input.files && input.files[0];
        input.value = '';
        if (!file) return;
        let scanner = null;
        try {
            scanner = showReceiptScannerModal(file, t.uploadingImage);
            const processed = await compressImageIfNeeded(file);
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result).split(',')[1]);
                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(processed);
            });
            scanner.updateStatus(t.analyzingReceipt);
            const { createExpenseFromReceipt } = await import('./api.mjs');
            const group = await createExpenseFromReceipt(null, null, null, base64);
            const expense = group.expenses[group.expenses.length - 1];
            if (window.gtag) { try { gtag('event', 'split_calc_scan'); } catch (e) { /* ignore */ } }
            window.location.href = `${PREFIX}/tabs/${group.id}/receipt/${expense.id}`;
        } catch (e) {
            if (scanner) scanner.close();
            const reason = e && e.message ? e.message : '';
            // Web scanning not available to this visitor (free scans used up,
            // bot check failed) -> the shared get-the-app sheet; the photo or
            // the service failed -> the page's own line, retry or type it in.
            // The server's reason rides along in small print either way.
            const { showGetAppSheet, scanRefusalKind } = await import('./ui.mjs');
            const kind = scanRefusalKind(reason);
            if (kind) showGetAppSheet({ kind, reason });
            else alert(btn.dataset.error ? `${btn.dataset.error}\n\n(${reason})` : reason);
        }
    });
}
